/**
 * Anmeldung gegen Authentik — Authorization Code mit PKCE.
 *
 * Kein Client-Geheimnis: Eine Oberfläche im Browser kann keines bewahren, und
 * eines zu hinterlegen wäre eine Zusicherung, die nicht trägt. PKCE ersetzt es
 * durch einen Nachweis, den nur der anfragende Browser führen kann.
 *
 * Die Token liegen im `sessionStorage`, nicht im `localStorage`: Sie enden mit
 * dem Tab. Das ist die richtige Abwägung für ein Werkzeug, das im Kundennetz
 * steht — ein liegengelassener Rechner soll keine offene Sitzung von gestern
 * haben. Der Preis ist eine Neuanmeldung pro Tab.
 *
 * `state` liegt ebenfalls im sessionStorage und wird beim Rückweg verglichen
 * (CUPR/Paket-10): sonst ist der Rückweg gegen untergeschobene Codes offen.
 */

const ausstellerRoh = import.meta.env["VITE_AUTHENTIK_ISSUER"] ?? "";
const aussteller = ausstellerRoh.replace(/\/$/, "");
const kundenId = import.meta.env["VITE_AUTHENTIK_CLIENT_ID"] ?? "";

function rueckwegAdresse(): string {
  const origin =
    typeof globalThis.location?.origin === "string"
      ? globalThis.location.origin
      : "http://localhost";
  return `${origin}/anmeldung/zurueck`;
}

const SPEICHER_TOKEN = "cubic.token";
const SPEICHER_PRUEFER = "cubic.pruefer";
const SPEICHER_ZIEL = "cubic.ziel";
const SPEICHER_STATE = "cubic.oauth_state";
const SPEICHER_ANMELDEVERSUCH = "cubic.anmeldeversuch";

const BASIS_FUER_ZIELPRUEFUNG = "https://cubic.invalid";

/**
 * Nur Pfade innerhalb von cubic studio dürfen eine Anmeldung überleben.
 *
 * `startsWith("/")` allein reicht nicht: `//fremder-host` ist ebenfalls eine
 * gültige URL und würde von `location.assign()` als externer Host verstanden.
 * Die URL-Normalisierung fängt zusätzlich Backslash-Varianten ab. Auf die
 * Anmeldeseite selbst springen wir nicht zurück, sonst entsteht nach einem
 * erfolgreichen Rückweg eine Schleife.
 */
export function sichererAnmeldeZielpfad(
  ziel: string | null | undefined,
): string {
  if (!ziel?.startsWith("/") || ziel.startsWith("//")) return "/";

  try {
    const url = new URL(ziel, BASIS_FUER_ZIELPRUEFUNG);
    if (url.origin !== BASIS_FUER_ZIELPRUEFUNG) return "/";
    if (
      url.pathname === "/anmeldung" ||
      url.pathname === "/anmeldung/zurueck"
    ) {
      return "/";
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return "/";
  }
}

export function istKonfiguriert(): boolean {
  return aussteller !== "" && kundenId !== "";
}

/* -------------------------------------------------------------------------
 * Endpunkte — aus der Discovery, nicht aus Stringbau
 * ---------------------------------------------------------------------- */

/**
 * Wo Authentik wirklich hört.
 *
 * Vorher wurden die Adressen zusammengesetzt: `${aussteller}/authorize/` und
 * `${aussteller}/token/`. Das ist falsch. Authentiks Discovery meldet
 *
 *   authorization_endpoint: https://…/application/o/authorize/
 *   token_endpoint:         https://…/application/o/token/
 *
 * — **ohne** den Anwendungsteil. Nur `jwks_uri` hängt an der Anwendung. Der
 * zusammengebaute Pfad lief also ins Leere, und zwar unabhängig davon, ob die
 * `.env` stimmte.
 *
 * Deshalb wird jetzt gefragt statt geraten. Das ist der Zweck von Discovery:
 * Ein Anbieter darf seine Adressen ändern, ohne dass wir es merken müssen.
 */
interface Endpunkte {
  authorization_endpoint: string;
  token_endpoint: string;
}

let endpunkteZwischenspeicher: Promise<Endpunkte> | null = null;

export function endpunkte(): Promise<Endpunkte> {
  endpunkteZwischenspeicher ??= (async () => {
    const antwort = await fetch(
      `${aussteller}/.well-known/openid-configuration`,
    );
    if (!antwort.ok) {
      throw new Error(
        `Authentik antwortet auf die Discovery nicht (${antwort.status}). ` +
          `Stimmt VITE_AUTHENTIK_ISSUER?`,
      );
    }
    const daten = (await antwort.json()) as Partial<Endpunkte>;
    if (!daten.authorization_endpoint || !daten.token_endpoint) {
      throw new Error("Discovery ohne Endpunkte.");
    }
    return {
      authorization_endpoint: daten.authorization_endpoint,
      token_endpoint: daten.token_endpoint,
    };
  })();
  // Ein Fehlschlag darf sich nicht festsetzen — sonst bleibt die Anmeldung
  // bis zum Neuladen kaputt, obwohl Authentik längst wieder da ist.
  return endpunkteZwischenspeicher.catch((fehler: unknown) => {
    endpunkteZwischenspeicher = null;
    throw fehler;
  });
}

/* -------------------------------------------------------------------------
 * PKCE
 * ---------------------------------------------------------------------- */

function zufallstext(laenge = 64): string {
  const bytes = new Uint8Array(laenge);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

function base64Url(bytes: Uint8Array): string {
  let roh = "";
  for (const byte of bytes) roh += String.fromCharCode(byte);
  return btoa(roh).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function pruefsumme(pruefer: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(pruefer),
  );
  return base64Url(new Uint8Array(digest));
}

/* -------------------------------------------------------------------------
 * Bremse gegen die Anmeldeschleife
 *
 * Ein 401 löst eine Neuanmeldung aus. Weist der Server das frische Token aus
 * demselben Grund wieder ab — deaktivierte Person, fehlende Zuordnung, ein
 * Ausfall, den er fälschlich als 401 meldet —, dann ist die nächste
 * Neuanmeldung nur eine weitere Runde. Und weil eine Runde durch Authentik ein
 * vollständiger Seitenneuaufbau ist, hilft keine Modulvariable dagegen: Sie
 * beginnt jedes Mal wieder bei ihrem Anfangswert. Der Vermerk muss den
 * Neuaufbau überleben, also liegt er im sessionStorage.
 * ---------------------------------------------------------------------- */

/** So lange nach einem Anmeldeversuch gilt ein erneuter 401 als Schleife. */
const ANMELDEVERSUCH_FENSTER_MS = 30_000;

export function merkeAnmeldeversuch(jetzt = Date.now()): void {
  sessionStorage.setItem(SPEICHER_ANMELDEVERSUCH, String(jetzt));
}

export function vergissAnmeldeversuch(): void {
  sessionStorage.removeItem(SPEICHER_ANMELDEVERSUCH);
}

/**
 * Reine Entscheidung — vitest-bar ohne sessionStorage und ohne Uhr.
 *
 * Ein unlesbarer Vermerk zählt bewusst nicht als Schleife: Im Zweifel lieber
 * eine Anmeldung zu viel anbieten als eine Person aussperren, die sich
 * regulär anmelden will.
 */
export function istAnmeldeschleife(
  vermerk: string | null,
  jetzt: number,
  fenster = ANMELDEVERSUCH_FENSTER_MS,
): boolean {
  if (!vermerk) return false;
  const zeit = Number(vermerk);
  if (!Number.isFinite(zeit)) return false;
  const alter = jetzt - zeit;
  return alter >= 0 && alter < fenster;
}

/** Dieselbe Entscheidung, gegen den echten Speicher. */
export function anmeldungDrehtSichImKreis(jetzt = Date.now()): boolean {
  return istAnmeldeschleife(
    sessionStorage.getItem(SPEICHER_ANMELDEVERSUCH),
    jetzt,
  );
}

/* -------------------------------------------------------------------------
 * Anmelden
 * ---------------------------------------------------------------------- */

export async function melde_an(zielPfad = globalThis.location.pathname) {
  const pruefer = zufallstext();
  const state = zufallstext(16);
  sessionStorage.setItem(SPEICHER_PRUEFER, pruefer);
  sessionStorage.setItem(SPEICHER_ZIEL, sichererAnmeldeZielpfad(zielPfad));
  sessionStorage.setItem(SPEICHER_STATE, state);
  merkeAnmeldeversuch();

  const { authorization_endpoint } = await endpunkte();
  const ziel = new URL(authorization_endpoint);
  ziel.searchParams.set("response_type", "code");
  ziel.searchParams.set("client_id", kundenId);
  ziel.searchParams.set("redirect_uri", rueckwegAdresse());
  ziel.searchParams.set("scope", "openid profile email");
  ziel.searchParams.set("code_challenge", await pruefsumme(pruefer));
  ziel.searchParams.set("code_challenge_method", "S256");
  ziel.searchParams.set("state", state);

  globalThis.location.assign(ziel.toString());
}

/**
 * Prüft den OAuth-`state` aus der Rückweg-URL gegen den gespeicherten Wert.
 * Reine Funktion — vitest-bar ohne DOM-Fetch.
 */
export function pruefeOauthState(
  erhalten: string | null | undefined,
  erwartet: string | null | undefined,
): boolean {
  if (!erhalten || !erwartet) return false;
  if (erhalten.length !== erwartet.length) return false;
  // zeitkonstanter Vergleich (kleine Strings, aber keine Kurzschlüsse)
  let diff = 0;
  for (let i = 0; i < erhalten.length; i++) {
    diff |= erhalten.charCodeAt(i) ^ erwartet.charCodeAt(i);
  }
  return diff === 0;
}

/**
 * Den Rückweg abschließen: Code gegen Token tauschen.
 *
 * Gibt den Pfad zurück, auf dem die Person vor der Anmeldung war — eine
 * Anmeldung soll nicht auf der Startseite enden, wenn jemand eine bestimmte
 * Seite öffnen wollte.
 */
export async function schliesse_anmeldung_ab(): Promise<string> {
  const params = new URLSearchParams(globalThis.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const pruefer = sessionStorage.getItem(SPEICHER_PRUEFER);
  const erwartetState = sessionStorage.getItem(SPEICHER_STATE);

  if (!pruefeOauthState(state, erwartetState)) {
    sessionStorage.removeItem(SPEICHER_PRUEFER);
    sessionStorage.removeItem(SPEICHER_STATE);
    throw new Error("Der Anmelde-Rückweg wurde abgewiesen (state).");
  }

  if (!code || !pruefer) throw new Error("Der Rückweg ist unvollständig.");

  const { token_endpoint } = await endpunkte();
  const antwort = await fetch(token_endpoint, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: rueckwegAdresse(),
      client_id: kundenId,
      code_verifier: pruefer,
    }),
  });

  if (!antwort.ok) {
    throw new Error(`Authentik hat den Tausch abgelehnt (${antwort.status}).`);
  }

  const daten = (await antwort.json()) as { access_token?: string };
  if (!daten.access_token) throw new Error("Antwort ohne Token.");

  sessionStorage.setItem(SPEICHER_TOKEN, daten.access_token);
  sessionStorage.removeItem(SPEICHER_PRUEFER);
  sessionStorage.removeItem(SPEICHER_STATE);

  const ziel = sichererAnmeldeZielpfad(
    sessionStorage.getItem(SPEICHER_ZIEL),
  );
  sessionStorage.removeItem(SPEICHER_ZIEL);
  return ziel;
}

export function token(): string | null {
  return sessionStorage.getItem(SPEICHER_TOKEN);
}

export function melde_ab() {
  sessionStorage.removeItem(SPEICHER_TOKEN);
  globalThis.location.assign("/");
}

/** Für Tests und Adapter — Speicherschlüssel. */
export const AUTH_SPEICHER = {
  token: SPEICHER_TOKEN,
  pruefer: SPEICHER_PRUEFER,
  ziel: SPEICHER_ZIEL,
  state: SPEICHER_STATE,
  anmeldeversuch: SPEICHER_ANMELDEVERSUCH,
} as const;
